import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import dataRoomApi from '../../../dataRoomApi';
import { DataRoomNdaSigner } from '../../../models/DataRoomNDA';
import { PaginatedData } from 'models/PaginatedData';
import { EMPTY_ARRAY } from 'data/global';

type UseNdaSignersQueryParams = {
  dataRoomId: string;
};

const NDA_SIGNERS_QUERY_KEY = 'data-room-nda-signers';

export default function useNdaSignersQuery({
  dataRoomId,
}: UseNdaSignersQueryParams) {
  const queryClient = useQueryClient();

  const getMixpanelProps = useGetMixpanelProps();

  const page = usePageParams();
  const { limit } = useNumberOfResultsPerPage();

  const {
    params: { sort, dataRoomNdaId, name, email, searchString },
  } = useQueryParams();

  const queryKey = [
    NDA_SIGNERS_QUERY_KEY,
    { dataRoomId, page, limit, sort, dataRoomNdaId, name, email, searchString },
  ];

  const { data, isLoading, refetch } = useQuery<
    PaginatedData<DataRoomNdaSigner>
  >({
    queryKey,
    queryFn: async () => {
      const { data } = await dataRoomApi.nda.getNdaSigners(dataRoomId, {
        $page: page,
        $limit: limit,
        $sort: sort,
        $searchString: searchString,
        dataRoomNdaId,
        name,
        email,
      });

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!dataRoomId,
  });

  const { items = EMPTY_ARRAY, totalItems = 0, totalPages = 0 } = data || {};

  const updateSigner = useCallback(
    (signer: Partial<DataRoomNdaSigner>) => {
      queryClient.setQueryData<PaginatedData<DataRoomNdaSigner>>(
        queryKey,
        (prev) =>
          prev && {
            ...prev,
            items: prev.items.map((s) =>
              s._id !== signer._id ? s : { ...s, ...signer },
            ),
          },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, JSON.stringify(queryKey)],
  );

  useInvalidateQueryOnUnmount(NDA_SIGNERS_QUERY_KEY);

  return {
    isLoading,
    totalPages,
    totalItems,
    items,
    updateSigner,
    refetch,
  };
}
